// src/pages/Admin/AdminCategories.jsx
import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../api/categories.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';
import { Loader } from '../../components/ui/Loader/Loader.jsx';

export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: '',
    sort_order: 0,
    is_active: true,
    icon: '',
  });
  const { success, error } = useToast();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setIsLoading(true);
    try {
      const response = await categoriesAPI.getAll({ is_active: true });
      setCategories(response.data.data || []);
    } catch (err) {
      error('Не удалось загрузить категории');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      error('Название и Slug обязательны');
      return;
    }

    try {
      if (editingId) {
        await categoriesAPI.update(editingId, formData);
        success('Категория обновлена');
      } else {
        await categoriesAPI.create(formData);
        success('Категория создана');
      }
      setFormData({ name: '', slug: '', description: '', parent_id: '', sort_order: 0, is_active: true, icon: '' });
      setEditingId(null);
      loadCategories();
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось сохранить категорию');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены?')) return;
    try {
      await categoriesAPI.delete(id);
      success('Категория удалена');
      loadCategories();
    } catch (err) {
      error('Не удалось удалить категорию');
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      parent_id: category.parent_id || '',
      sort_order: category.sort_order || 0,
      is_active: category.is_active,
      icon: category.icon || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', slug: '', description: '', parent_id: '', sort_order: 0, is_active: true, icon: '' });
  };

  if (isLoading) {
    return <Loader text="Загрузка категорий..." />;
  }

  return (
    <div>
      {/* Форма */}
      <div style={{
        backgroundColor: 'rgb(var(--c-surface))',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          {editingId ? 'Редактировать категорию' : 'Новая категория'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Название *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="Slug *"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
            />
            <Input
              label="Описание"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Родительская категория
              </label>
              <select
                value={formData.parent_id}
                onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgb(var(--c-border))',
                  fontSize: '14px',
                  outline: 'none',
                }}
              >
                <option value="">Корневая</option>
                {categories.filter(c => c.id !== editingId).map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <Input
              label="Порядок сортировки"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Активна
              </label>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
            <Button type="submit">{editingId ? 'Обновить' : 'Создать'}</Button>
            {editingId && (
              <Button type="button" variant="secondary" onClick={cancelEdit}>
                Отмена
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Список */}
      <div style={{
        backgroundColor: 'rgb(var(--c-surface))',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgb(var(--c-bg))' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))' }}>Название</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))' }}>Slug</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))' }}>Родитель</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))' }}>Статус</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: 'rgb(var(--c-text-secondary))' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id} style={{ borderTop: '1px solid rgb(var(--c-surface-2))' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 500 }}>{cat.name}</td>
                  <td style={{ padding: '12px 16px', color: 'rgb(var(--c-text-secondary))' }}>{cat.slug}</td>
                  <td style={{ padding: '12px 16px', color: 'rgb(var(--c-text-secondary))' }}>
                    {categories.find(c => c.id === cat.parent_id)?.name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '100px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: cat.is_active ? '#D4EDDA' : '#F8D7DA',
                      color: cat.is_active ? '#155724' : '#721C24',
                    }}>
                      {cat.is_active ? 'Активна' : 'Неактивна'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(cat)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: 'rgb(var(--c-accent))',
                        }}
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: 'rgb(var(--c-danger))',
                        }}
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};